import { useRef } from "react";
import { useObserve } from "@legendapp/state/react";
import {
  usePlaybackMediator,
  useTimelinePlayback,
  useVideoPlayback,
  useVisTimeline,
} from "@/contexts/playback-context";
import { useBufferController } from "@/services/buffer-controller";
import { useViewSettings } from "@/view-settings";
import { calcZoomLevel } from "../utils";
import { DataSet } from "vis-data";
import {
  TIMELINE_ITEM_VIEW,
  TimelineGroupConstants,
} from "../constants/timeline";

export const TIME_MARKER_ID = "time-cursor";

export interface TimelineState {
  isZooming: boolean;
  isDragging: boolean;
  isFocusAnimating: boolean;
  prevZoomLevel: number | null;
}

interface UseTimelineStateProps {
  dataset: DataSet<object, string>;
  groups: DataSet<object, string>;
  startBufferObserver: (viewMode?: "detections" | "snapshots") => void;
}

export const useTimelineState = ({
  dataset,
  groups,
  startBufferObserver,
}: UseTimelineStateProps) => {
  const timelineRef = useVisTimeline();
  const timelinePlayback = useTimelinePlayback();
  const videoPlayback = useVideoPlayback();
  const mediator = usePlaybackMediator();
  const viewSettings = useViewSettings();
  const [tracksBuffer, _, detectionsBuffer, reprojectionsBuffer] =
    useBufferController();

  const state = useRef<TimelineState>({
    isZooming: false,
    isDragging: false,
    isFocusAnimating: false,
    prevZoomLevel: null,
  });

  // Keep cursor visible by adjusting visible window
  useObserve(() => {
    const currentTime = timelinePlayback.currentTime.get();

    if (!timelineRef.current || state.current.isFocusAnimating) return;

    const { start, end } = timelineRef.current.getWindow();
    const windowDuration = end.getTime() - start.getTime();
    const shift = windowDuration / 20;

    if (currentTime >= end.getTime() && !state.current.isDragging) {
      state.current.isFocusAnimating = true;
      timelineRef.current.setWindow(
        new Date(currentTime - shift),
        new Date(currentTime + windowDuration - shift),
        { animation: windowDuration < 10000 ? false : true },
        () => {
          state.current.isFocusAnimating = false;
        }
      );
    }
  });

  // Handle time marker updates
  useObserve(() => {
    const currentTime = timelinePlayback.currentTime.get();

    if (currentTime) {
      try {
        timelineRef.current?.setCustomTime(currentTime, TIME_MARKER_ID);
      } catch {
        timelineRef.current?.addCustomTime(currentTime, TIME_MARKER_ID);
      }
    }
  });

  // Handle video continue time
  useObserve(() => {
    const currentTime = timelinePlayback.currentTime.get();
    const timeToContinue = videoPlayback.videoContext.timeToContinue.get();

    if (
      currentTime &&
      timeToContinue &&
      currentTime > timeToContinue.getTime()
    ) {
      videoPlayback.videoContext.timeToContinue.set(null);
      videoPlayback.dashPlayer.play();
    }
  });

  // Display video duration in timeline
  useObserve(() => {
    const startTime = videoPlayback.videoContext.startTime.get();
    const endTime = videoPlayback.videoContext.endTime.get();

    if (startTime && endTime) {
      groups.update({
        id: TimelineGroupConstants.VideoBuffer,
        content: "Video",
      });
      dataset.update({
        id: TimelineGroupConstants.VideoBuffer,
        content: TimelineGroupConstants.VideoBuffer,
        group: TimelineGroupConstants.VideoBuffer,
        start: startTime,
        end: endTime,
        view: TIMELINE_ITEM_VIEW.TRACK,
        className: TIMELINE_ITEM_VIEW.TRACK,
      });
    }
  });

  const MINIMUM_OFFSET_IN_MS = 40;

  // Continue playing when new buffer is available (detections mode)
  useObserve(reprojectionsBuffer.timeRanges.timeRanges$, () => {
    const isPending = timelinePlayback.playbackState.peek() === "pending";
    const currentTime = timelinePlayback.currentTime.peek();
    const videMode = viewSettings.viewMode.peek();

    if (!isPending || videMode !== "detections") return;

    const [isBufferAvailable] =
      reprojectionsBuffer.timeRanges.isBufferAvailable(
        currentTime + MINIMUM_OFFSET_IN_MS
      );

    if (isBufferAvailable) {
      mediator.play();
    }
  });

  // Continue playing when new buffer is available (detections mode)
  useObserve(tracksBuffer.timeRanges.timeRanges$, () => {
    const isPending = timelinePlayback.playbackState.peek() === "pending";
    const currentTime = timelinePlayback.currentTime.peek();
    const videMode = viewSettings.viewMode.peek();

    if (!isPending || videMode !== "snapshots") return;

    const [isBufferAvailable] = tracksBuffer.timeRanges.isBufferAvailable(
      currentTime + MINIMUM_OFFSET_IN_MS
    );

    if (isBufferAvailable) {
      mediator.play();
    }
  });

  // Handle live mode updates
  useObserve(() => {
    const isLive = timelinePlayback.liveMeta.isLive.get();
    const liveStart = timelinePlayback.liveMeta.start.peek();

    if (isLive && liveStart && timelineRef.current) {
      try {
        timelineRef.current.addCustomTime(liveStart, TIME_MARKER_ID);
      } catch {
        timelineRef.current.setCustomTime(liveStart, TIME_MARKER_ID);
      }

      timelineRef.current.moveTo(
        liveStart,
        { animation: false, zoom: false },
        () => {
          const window = timelineRef.current?.getWindow();
          if (window) {
            const newZoom = calcZoomLevel(window.start, window.end);
            timelinePlayback.zoomLevel.set(newZoom);
          }
        }
      );

      groups.clear();
      dataset.clear();
      startBufferObserver();

      dataset.add({
        id: "START",
        start: new Date(0),
        end: liveStart,
        type: "background",
      });
    }
  });

  // Handle history mode updates
  useObserve(() => {
    const dateRange = timelinePlayback.dateRange.get();
    const { start: startTime, end: endTime } = dateRange;

    if (startTime && endTime && timelineRef.current) {
      try {
        timelineRef.current.addCustomTime(startTime, TIME_MARKER_ID);
      } catch {
        timelineRef.current.setCustomTime(startTime, TIME_MARKER_ID);
      }

      groups.clear();
      dataset.clear();
      startBufferObserver(viewSettings.viewMode.peek());

      dataset.add({
        id: "START",
        start: new Date(0),
        end: startTime,
        type: "background",
      });

      dataset.add({
        id: "END",
        start: endTime,
        end: new Date(Infinity),
        type: "background",
      });

      const windowStart = new Date(startTime.getTime() - 1000 * 10);
      const windowEnd = new Date(endTime.getTime() + 1000 * 10);

      timelineRef.current.setOptions({
        start: windowStart,
        end: windowEnd,
      });

      if (window) {
        const newZoom = calcZoomLevel(windowStart, windowEnd);
        timelinePlayback.zoomLevel.set(newZoom);
      }

      timelinePlayback.currentTime.set(startTime.getTime());
    }
  });

  const updateZoomLevel = () => {
    const window = timelineRef.current?.getWindow();
    if (!window) return;

    const windowDuration = window.end.getTime() - window.start.getTime();
    const newZoom = calcZoomLevel(window.start, window.end, false);

    if (state.current.prevZoomLevel === null) {
      state.current.prevZoomLevel = windowDuration;
    }

    if (state.current.prevZoomLevel !== windowDuration) {
      timelinePlayback.zoomLevel.set(newZoom);
      state.current.prevZoomLevel = windowDuration;
    }
  };

  return {
    timelinePlayback,
    videoPlayback,
    mediator,
    viewSettings,
    tracksBuffer,
    detectionsBuffer,
    reprojectionsBuffer,
    state: state.current,
    updateZoomLevel,
  };
};
